import React, { useState, useEffect } from 'react';
import './App.css';
import './theme.css';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import RequireAuth from './hoc/requireAuth';

import AppSidebar from './Components/AppSidebar';
import TopNavBar from './Components/TopNavBar';

import Login from './Views/Login';
import Register from './Views/Register';
import SubscriberLogin from './Views/SubscriberLogin';
import SubscriberRegisterEmail from './Views/SubscriberRegisterEmail';
import SubscriberRegisterPhone from './Views/SubscriberRegisterPhone';
import SubscriberVerificationEmail from './Views/SubscriberVerificationEmail';
import SubscriberVippsLogin from './Views/SubscriberVippsLogin';

import { Home } from './modules/Home';
import { Company } from './modules/Company';
import { AddCompany } from './modules/AddCompany';
import { EditCompany } from './modules/EditCompany';
import { ViewCompany } from './modules/ViewCompany';
import { Provider } from './modules/Provider';
import { AddProvider } from './modules/AddProvider';
import { EditProvider } from './modules/EditProvider';
import { Service } from './modules/Service';
import { AddService } from './modules/AddService';
import { EditService } from './modules/EditService';
import { SelectProvider } from './modules/SelectProvider';
import { SelectService } from './modules/SelectService';
import { Role } from './modules/Role';
import { VerificationLog } from './modules/VerificationLog';
import UserAnalytics from './Views/UserAnalytics';
import LoginStatsDashboard from './Views/LoginStatsDashboard';
import ApiUsage from './Views/ApiUsage';
import AuditLogs from './Views/AuditLogs';
import Notifications from './Views/Notifications';
import Opportunities from './Views/Opportunities';
import MainCompanies from './Views/MainCompanies';
import { Unauthorized } from './modules/Unauthorized';

function AppShell() {
  const { auth } = useAuth();
  const [sidebar, setSidebar] = useState('sidebar sidebar-offcanvas');
  const [theme, setTheme] = useState(
    () =>
      localStorage.getItem('ivs-theme') ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('ivs-sidebar-collapsed') === 'true'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ivs-theme', theme);
  }, [theme]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-icon-only', sidebarCollapsed);
    localStorage.setItem('ivs-sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const toggleSidebar = () => {
    setSidebar((prev) =>
      prev.includes('active')
        ? 'sidebar sidebar-offcanvas'
        : 'sidebar sidebar-offcanvas active'
    );
  };

  return (
    <div className="App" id="outer-container">
      <div id="container-scroller">
        {auth && (
          <TopNavBar
            toggleSidebar={toggleSidebar}
            theme={theme}
            toggleTheme={toggleTheme}
            sidebarCollapsed={sidebarCollapsed}
            toggleCollapsed={toggleCollapsed}
          />
        )}

        <div className="container-fluid page-body-wrapper pt-0">
          {auth && <AppSidebar sidebar={sidebar} />}

          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/subscriber/login" element={<SubscriberLogin />} />
            <Route path="/subscriber/verification-email" element={<SubscriberVerificationEmail />} />
            <Route path="/subscriber/register-email" element={<SubscriberRegisterEmail />} />
            <Route path="/subscriber/register-phone" element={<SubscriberRegisterPhone />} />
            <Route path="/subscriber/vipps-login" element={<SubscriberVippsLogin />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            <Route path="/" element={<RequireAuth><Navigate to="/dashboard" replace /></RequireAuth>} />
            <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
            <Route path="/dashboard" element={<RequireAuth><Home /></RequireAuth>} />
            <Route path="/logs" element={<RequireAuth><VerificationLog /></RequireAuth>} />
            <Route path="/log" element={<RequireAuth><VerificationLog /></RequireAuth>} />
            <Route path="/analytics" element={<RequireAuth><UserAnalytics /></RequireAuth>} />
            <Route path="/dailystats" element={<RequireAuth><LoginStatsDashboard /></RequireAuth>} />
            <Route path="/api-usage" element={<RequireAuth><ApiUsage /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/companies/edit/:id" element={<RequireAuth><EditCompany /></RequireAuth>} />
            <Route path="/companies/view/:id" element={<RequireAuth><ViewCompany /></RequireAuth>} />
            <Route path="/selectservice" element={<RequireAuth><SelectService /></RequireAuth>} />
            <Route path="/selectservice/:id" element={<RequireAuth><SelectService /></RequireAuth>} />
            <Route path="/selectprovider" element={<RequireAuth><SelectProvider /></RequireAuth>} />
            <Route path="/selectprovider/:id" element={<RequireAuth><SelectProvider /></RequireAuth>} />

            <Route path="/companies" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><Company /></RequireAuth>} />
            <Route path="/addcompany" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><AddCompany /></RequireAuth>} />
            <Route path="/services" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><Service /></RequireAuth>} />
            <Route path="/addservice" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><AddService /></RequireAuth>} />
            <Route path="/service/edit/:id" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><EditService /></RequireAuth>} />
            <Route path="/providers" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><Provider /></RequireAuth>} />
            <Route path="/addprovider" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><AddProvider /></RequireAuth>} />
            <Route path="/provider/edit/:id" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><EditProvider /></RequireAuth>} />
            <Route path="/audit-logs" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><AuditLogs /></RequireAuth>} />
            <Route path="/main-companies" element={<RequireAuth roles={['Admin', 'SuperAdmin']}><MainCompanies /></RequireAuth>} />

            <Route path="/roles" element={<RequireAuth roles={['SuperAdmin']}><Role /></RequireAuth>} />
            <Route path="/opportunities" element={<RequireAuth roles={['SuperAdmin']}><Opportunities /></RequireAuth>} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
